import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useCrypto } from './crypto';
import { api } from '../api/client';
import { encrypt, decrypt } from '@mysecureroom/crypto';

// Types
export interface ValueRecord {
  id: string;
  value_name: string;
  value_description: string | null;
  rank: number;
  is_custom: number;
  life_area_ids: string[]; // Life areas this value applies to
  created_at: string;
  updated_at: string;
}

export interface PresetValue {
  name: string;
  category: string;
  description?: string;
}

// Preset values list (80+ common values across 15 categories)
export const PRESET_VALUES: PresetValue[] = [
  // Character (12 values)
  { name: 'Authenticity', category: 'Character', description: 'Being true to yourself and expressing your genuine self' },
  { name: 'Courage', category: 'Character', description: 'Acting despite fear to do what is right' },
  { name: 'Honesty', category: 'Character', description: 'Being truthful and sincere in words and actions' },
  { name: 'Integrity', category: 'Character', description: 'Adhering to moral and ethical principles' },
  { name: 'Patience', category: 'Character', description: 'Remaining calm and tolerant in difficult situations' },
  { name: 'Perseverance', category: 'Character', description: 'Continuing despite obstacles and setbacks' },
  { name: 'Responsibility', category: 'Character', description: 'Being accountable for your actions and commitments' },
  { name: 'Self-discipline', category: 'Character', description: 'Controlling your impulses and staying focused' },
  { name: 'Truth', category: 'Character', description: 'Seeking and upholding what is real and accurate' },
  { name: 'Resilience', category: 'Character', description: 'Recovering quickly from difficulties and adversity' },
  { name: 'Humility', category: 'Character', description: 'Having a modest view of your own importance' },
  { name: 'Transparency', category: 'Character', description: 'Being open and honest about your intentions' },

  // Success (5 values)
  { name: 'Achievement', category: 'Success', description: 'Accomplishing goals and reaching milestones' },
  { name: 'Leadership', category: 'Success', description: 'Guiding and inspiring others toward shared goals' },
  { name: 'Success', category: 'Success', description: 'Attaining desired outcomes and recognition' },
  { name: 'Wealth', category: 'Success', description: 'Building financial abundance and security' },
  { name: 'Ambition', category: 'Success', description: 'Striving for excellence and advancement' },

  // Experience (5 values)
  { name: 'Adventure', category: 'Experience', description: 'Seeking new and exciting experiences' },
  { name: 'Humor', category: 'Experience', description: 'Finding and sharing laughter and lightness' },
  { name: 'Joy', category: 'Experience', description: 'Experiencing happiness and delight' },
  { name: 'Excitement', category: 'Experience', description: 'Embracing thrilling and stimulating moments' },
  { name: 'Playfulness', category: 'Experience', description: 'Approaching life with fun and spontaneity' },

  // Lifestyle (6 values)
  { name: 'Balance', category: 'Lifestyle', description: 'Maintaining harmony across life areas' },
  { name: 'Freedom', category: 'Lifestyle', description: 'Living without constraints or limitations' },
  { name: 'Independence', category: 'Lifestyle', description: 'Being self-reliant and autonomous' },
  { name: 'Simplicity', category: 'Lifestyle', description: 'Living with minimal complexity and clutter' },
  { name: 'Flexibility', category: 'Lifestyle', description: 'Adapting easily to changing circumstances' },
  { name: 'Comfort', category: 'Lifestyle', description: 'Enjoying ease and pleasant conditions' },

  // Relationships (12 values)
  { name: 'Compassion', category: 'Relationships', description: 'Showing deep care and concern for others' },
  { name: 'Family', category: 'Relationships', description: 'Prioritizing bonds with loved ones' },
  { name: 'Kindness', category: 'Relationships', description: 'Being gentle, considerate, and helpful' },
  { name: 'Love', category: 'Relationships', description: 'Giving and receiving deep affection' },
  { name: 'Loyalty', category: 'Relationships', description: 'Remaining faithful and devoted to others' },
  { name: 'Respect', category: 'Relationships', description: 'Honoring the dignity and worth of others' },
  { name: 'Teamwork', category: 'Relationships', description: 'Collaborating effectively with others' },
  { name: 'Trust', category: 'Relationships', description: 'Building and maintaining reliability' },
  { name: 'Empathy', category: 'Relationships', description: 'Understanding and sharing feelings of others' },
  { name: 'Generosity', category: 'Relationships', description: 'Giving freely without expecting return' },
  { name: 'Friendship', category: 'Relationships', description: 'Cultivating meaningful connections' },
  { name: 'Connection', category: 'Relationships', description: 'Building deep bonds with others' },

  // Growth (6 values)
  { name: 'Curiosity', category: 'Growth', description: 'Seeking to understand and explore' },
  { name: 'Growth', category: 'Growth', description: 'Continuously developing and evolving' },
  { name: 'Knowledge', category: 'Growth', description: 'Acquiring understanding and information' },
  { name: 'Learning', category: 'Growth', description: 'Actively gaining new skills and insights' },
  { name: 'Wisdom', category: 'Growth', description: 'Applying knowledge with experience and judgment' },
  { name: 'Self-improvement', category: 'Growth', description: 'Striving to become a better version of yourself' },

  // Wellbeing (6 values)
  { name: 'Health', category: 'Wellbeing', description: 'Maintaining physical and mental wellness' },
  { name: 'Mindfulness', category: 'Wellbeing', description: 'Being present and aware in each moment' },
  { name: 'Peace', category: 'Wellbeing', description: 'Experiencing inner calm and tranquility' },
  { name: 'Harmony', category: 'Wellbeing', description: 'Creating balance and unity within yourself' },
  { name: 'Vitality', category: 'Wellbeing', description: 'Living with energy and enthusiasm' },
  { name: 'Rest', category: 'Wellbeing', description: 'Honoring the need for recovery and renewal' },

  // Society (5 values)
  { name: 'Justice', category: 'Society', description: 'Upholding fairness and equality for all' },
  { name: 'Service', category: 'Society', description: 'Contributing to the wellbeing of others' },
  { name: 'Community', category: 'Society', description: 'Building and supporting collective strength' },
  { name: 'Equality', category: 'Society', description: 'Ensuring equal rights and opportunities' },
  { name: 'Contribution', category: 'Society', description: 'Making a positive impact on society' },

  // Environment (4 values)
  { name: 'Nature', category: 'Environment', description: 'Connecting with and appreciating the natural world' },
  { name: 'Sustainability', category: 'Environment', description: 'Living in ways that preserve resources' },
  { name: 'Conservation', category: 'Environment', description: 'Protecting and preserving natural habitats' },
  { name: 'Stewardship', category: 'Environment', description: 'Taking responsibility for environmental care' },

  // Mindset (5 values)
  { name: 'Optimism', category: 'Mindset', description: 'Expecting positive outcomes and possibilities' },
  { name: 'Gratitude', category: 'Mindset', description: 'Appreciating what you have and receive' },
  { name: 'Open-mindedness', category: 'Mindset', description: 'Welcoming new ideas and perspectives' },
  { name: 'Positivity', category: 'Mindset', description: 'Focusing on constructive and hopeful thoughts' },
  { name: 'Hope', category: 'Mindset', description: 'Believing in better possibilities ahead' },

  // Meaning (4 values)
  { name: 'Purpose', category: 'Meaning', description: 'Living with direction and intention' },
  { name: 'Spirituality', category: 'Meaning', description: 'Connecting to something greater than yourself' },
  { name: 'Fulfillment', category: 'Meaning', description: 'Finding deep satisfaction in life' },
  { name: 'Significance', category: 'Meaning', description: 'Making your life matter and count' },

  // Standards (4 values)
  { name: 'Quality', category: 'Standards', description: 'Maintaining high standards in all you do' },
  { name: 'Excellence', category: 'Standards', description: 'Striving for the highest level of performance' },
  { name: 'Professionalism', category: 'Standards', description: 'Conducting yourself with competence and ethics' },
  { name: 'Precision', category: 'Standards', description: 'Being exact and accurate in your work' },

  // Stability (4 values)
  { name: 'Security', category: 'Stability', description: 'Ensuring safety and protection from harm' },
  { name: 'Stability', category: 'Stability', description: 'Maintaining consistency and predictability' },
  { name: 'Reliability', category: 'Stability', description: 'Being dependable and trustworthy' },
  { name: 'Consistency', category: 'Stability', description: 'Acting in alignment with your principles' },

  // Culture (4 values)
  { name: 'Tradition', category: 'Culture', description: 'Honoring established customs and practices' },
  { name: 'Innovation', category: 'Culture', description: 'Creating new and better ways of doing things' },
  { name: 'Creativity', category: 'Culture', description: 'Expressing imagination and original ideas' },
  { name: 'Diversity', category: 'Culture', description: 'Embracing and celebrating differences' },

  // Energy (4 values)
  { name: 'Passion', category: 'Energy', description: 'Pursuing what deeply excites and moves you' },
  { name: 'Enthusiasm', category: 'Energy', description: 'Approaching life with energy and excitement' },
  { name: 'Determination', category: 'Energy', description: 'Pushing forward with unwavering resolve' },
  { name: 'Focus', category: 'Energy', description: 'Concentrating your energy on what matters most' },
];

interface ValuesState {
  values: ValueRecord[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

interface ValuesContextValue extends ValuesState {
  initialize: () => Promise<void>;
  addValue: (valueName: string, description?: string) => Promise<void>;
  rankValues: (rankedIds: string[]) => Promise<void>;
  removeValue: (id: string) => Promise<void>;
  updateValueAreas: (valueId: string, lifeAreaIds: string[]) => Promise<void>;
  getValuesByArea: (areaId: string) => ValueRecord[];
  clear: () => void;
}

const ValuesContext = createContext<ValuesContextValue | null>(null);

export function ValuesProvider({ children }: { children: ReactNode }) {
  const crypto = useCrypto();
  const [state, setState] = useState<ValuesState>({
    values: [],
    loading: false,
    error: null,
    initialized: false,
  });

  // Encryption helpers
  const encryptText = async (text: string): Promise<string> => {
    if (!crypto.encryptionKey) {
      throw new Error('Encryption key not available');
    }
    return encrypt(crypto.encryptionKey, text);
  };

  const decryptText = async (encrypted: string): Promise<string> => {
    if (!crypto.encryptionKey) {
      throw new Error('Encryption key not available');
    }
    return decrypt(crypto.encryptionKey, encrypted);
  };

  // Initialize / fetch all values
  const initialize = async () => {
    // Don't initialize if encryption key isn't ready yet
    if (!crypto.encryptionKey) {
      console.log('Waiting for encryption key...');
      return;
    }

    setState(s => ({ ...s, loading: true, error: null }));

    try {
      const result = await api.get<{ values: ValueRecord[] }>('/values');

      // Decrypt all values and filter out any with null IDs
      const decryptedValues = await Promise.all(
        result.values
          .filter(v => v.id != null) // Filter out null/undefined IDs
          .map(async (v) => ({
            ...v,
            value_name: await decryptText(v.value_name),
            value_description: v.value_description ? await decryptText(v.value_description) : null,
          }))
      );

      console.log('Loaded values:', decryptedValues);

      setState({
        values: decryptedValues.sort((a, b) => a.rank - b.rank),
        loading: false,
        error: null,
        initialized: true,
      });
    } catch (error) {
      console.error('Failed to load values:', error);
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load values',
        initialized: true, // Set initialized even on error to prevent infinite loops
      }));
    }
  };

  // Add a new value
  const addValue = async (valueName: string, description?: string) => {
    setState(s => ({ ...s, loading: true, error: null }));

    try {
      const encryptedName = await encryptText(valueName);
      const encryptedDescription = description ? await encryptText(description) : undefined;

      const result = await api.post<{ value: ValueRecord }>('/values', {
        value_name: encryptedName,
        value_description: encryptedDescription,
      });

      const decryptedValue = {
        ...result.value,
        value_name: await decryptText(result.value.value_name),
        value_description: result.value.value_description ? await decryptText(result.value.value_description) : null,
      };

      setState(s => ({
        ...s,
        values: [...s.values, decryptedValue].sort((a, b) => a.rank - b.rank),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to add value',
      }));
    }
  };

  // Rank/reorder values
  const rankValues = async (rankedIds: string[]) => {
    setState(s => ({ ...s, loading: true, error: null }));

    try {
      const result = await api.patch<{ values: ValueRecord[] }>('/values/rank', {
        ranked_ids: rankedIds,
      });

      // Decrypt all values
      const decryptedValues = await Promise.all(
        result.values.map(async (v) => ({
          ...v,
          value_name: await decryptText(v.value_name),
          value_description: v.value_description ? await decryptText(v.value_description) : null,
        }))
      );

      setState(s => ({
        ...s,
        values: decryptedValues.sort((a, b) => a.rank - b.rank),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to rank values',
      }));
    }
  };

  // Remove a value
  const removeValue = async (id: string) => {
    setState(s => ({ ...s, loading: true, error: null }));

    try {
      await api.delete(`/values/${id}`);
      setState(s => ({
        ...s,
        values: s.values.filter((v) => v.id !== id),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to remove value',
      }));
    }
  };

  const updateValueAreas = async (valueId: string, lifeAreaIds: string[]) => {
    setState(s => ({ ...s, loading: true, error: null }));

    try {
      await api.patch(`/values/${valueId}/areas`, { life_area_ids: lifeAreaIds });
      setState(s => ({
        ...s,
        values: s.values.map((v) => v.id === valueId ? { ...v, life_area_ids: lifeAreaIds } : v),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to update value areas',
      }));
    }
  };

  const getValuesByArea = (areaId: string): ValueRecord[] => {
    return state.values.filter(v => v.life_area_ids.includes(areaId));
  };

  // Clear on logout
  const clear = () => {
    setState({
      values: [],
      loading: false,
      error: null,
      initialized: false,
    });
  };

  const value: ValuesContextValue = {
    ...state,
    initialize,
    addValue,
    rankValues,
    removeValue,
    updateValueAreas,
    getValuesByArea,
    clear,
  };

  return <ValuesContext.Provider value={value}>{children}</ValuesContext.Provider>;
}

export function useValues() {
  const context = useContext(ValuesContext);
  if (!context) {
    throw new Error('useValues must be used within ValuesProvider');
  }
  return context;
}
